// src/components/ui/Badge.tsx
//
// Soft-tinted status chip in the Preline pattern: pale background + saturated
// foreground text + optional leading dot. Use for severity buckets ("2
// critical"), due-date tone ("逾期 1 天"), category tags, neutral status.
//
// Variants:
//   - critical / warn / info / success — semantic severity (bg + fg + dot
//     from severity tokens)
//   - primary — brand-coloured chip (bg-primary-soft-bg + text-primary-soft-fg)
//   - neutral — zinc fill, for non-semantic tags
//
// The chip itself is non-interactive by default; wrap with a button if you
// want it clickable (AlertsBanner does this for severity-bucketed pills).

import * as React from 'react';
import { cn } from '@/lib/utils';

export type BadgeVariant =
  | 'primary'
  | 'critical'
  | 'warn'
  | 'info'
  | 'success'
  | 'neutral';
export type BadgeSize = 'sm' | 'md';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  /** Render a small saturated dot before the text — useful for severity chips. */
  leadingDot?: boolean;
}

const VARIANT_CLASSES: Record<BadgeVariant, { bg: string; fg: string; dot: string }> = {
  primary: {
    bg: 'bg-primary-soft-bg',
    fg: 'text-primary-soft-fg',
    dot: 'bg-primary',
  },
  critical: {
    bg: 'bg-severity-critical-bg',
    fg: 'text-severity-critical-fg',
    dot: 'bg-severity-critical-dot',
  },
  warn: {
    bg: 'bg-severity-warn-bg',
    fg: 'text-severity-warn-fg',
    dot: 'bg-severity-warn-dot',
  },
  info: {
    bg: 'bg-severity-info-bg',
    fg: 'text-severity-info-fg',
    dot: 'bg-severity-info-dot',
  },
  success: {
    bg: 'bg-severity-success-bg',
    fg: 'text-severity-success-fg',
    dot: 'bg-severity-success-dot',
  },
  neutral: {
    bg: 'bg-muted',
    fg: 'text-ink-700',
    dot: 'bg-ink-500',
  },
};

const SIZE_CLASSES: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs gap-1',
  md: 'px-2.5 py-1 text-sm gap-1.5',
};

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { variant = 'neutral', size = 'sm', leadingDot, className, children, ...rest },
  ref,
) {
  const v = VARIANT_CLASSES[variant];
  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center rounded-md font-medium',
        SIZE_CLASSES[size],
        v.bg,
        v.fg,
        className,
      )}
      {...rest}
    >
      {leadingDot && (
        <span
          aria-hidden
          className={cn('inline-block h-1.5 w-1.5 rounded-full', v.dot)}
        />
      )}
      {children}
    </span>
  );
});
